import React, { useState, useEffect, useRef } from 'react';
import { useExpense } from '../../context/ExpenseContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Download, Upload, FolderOpen, Save, 
  Tag, CreditCard, Users, Loader2, AlertTriangle, X
} from 'lucide-react';

const ExpenseSettings = ({ role }) => {
  const { lov, fetchLOV, addLOVItem, removeLOVItem, getLOVRaw, createBackup, restoreBackup } = useExpense();
  
  const fileInputRef = useRef(null);
  
  // Bug 5 Fix: Check if user is super admin
  const isSuperAdmin = role === 'super_admin';
  
  const [activeTab, setActiveTab] = useState('categories');
  const [lovData, setLovData] = useState({
    categories: [],
    payment_modes: [],
    payers: []
  });
  const [newItem, setNewItem] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: '', value: '' });

  useEffect(() => {
    loadLOVData();
  }, []);

  // Clear input field when switching tabs
  useEffect(() => {
    setNewItem('');
  }, [activeTab]);

  const loadLOVData = async () => {
    setLoading(true);
    const [cats, payments, payers] = await Promise.all([
      getLOVRaw('categories'),
      getLOVRaw('payment_modes'),
      getLOVRaw('payers')
    ]);
    setLovData({
      categories: cats?.items || [],
      payment_modes: payments?.items || [],
      payers: payers?.items || []
    });
    setLoading(false);
  };

  // Bug 10 Fix: Check for duplicate values before adding
  const handleAddItem = async () => {
    if (!newItem.trim()) {
      toast.error('Please enter a value');
      return;
    }
    
    // Bug 10 Fix: Check for duplicate (case-insensitive)
    const trimmedValue = newItem.trim();
    const existingItems = lovData[activeTab] || [];
    const isDuplicate = existingItems.some(
      item => item.value.toLowerCase() === trimmedValue.toLowerCase()
    );
    
    if (isDuplicate) {
      toast.error(`"${trimmedValue}" already exists in ${activeTab.replace('_', ' ')}`);
      return;
    }
    
    setLoading(true);
    const result = await addLOVItem(activeTab, trimmedValue);
    setLoading(false);
    
    if (result.success) {
      toast.success(`Added "${trimmedValue}" to ${activeTab.replace('_', ' ')}`);
      setNewItem('');
      loadLOVData();
    } else {
      toast.error(result.error);
    }
  };

  const handleRemoveItem = async () => {
    setLoading(true);
    const result = await removeLOVItem(deleteConfirm.type, deleteConfirm.id);
    setLoading(false);
    
    if (result.success) {
      toast.success(`Removed "${deleteConfirm.value}"`);
      loadLOVData();
    } else {
      toast.error(result.error);
    }
    setDeleteConfirm({ open: false, type: '', id: '', value: '' });
  };

  const handleBackup = async () => {
    // Check super admin permission first
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can download backups');
      return;
    }
    
    setLoading(true);
    const result = await createBackup();
    setLoading(false);
    
    if (result.success) {
      toast.success('Backup downloaded successfully');
    } else {
      // Only show error toast if it's not a permission issue (already handled above)
      if (!result.error?.includes('Super Admin')) {
        toast.error(result.error);
      }
    }
  };

  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    
    // Bug 3 Fix: Always reset the file input to allow re-selection
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON backup file');
      return;
    }
    
    setLoading(true);
    const result = await restoreBackup(file);
    setLoading(false);
    
    if (result.success) {
      toast.success(`Restored ${result.data.expenses_restored} expenses`);
      loadLOVData();
    } else {
      toast.error(result.error);
    }
  };

  const getIconForTab = (tab) => {
    switch (tab) {
      case 'categories': return <Tag className="w-4 h-4" />;
      case 'payment_modes': return <CreditCard className="w-4 h-4" />;
      case 'payers': return <Users className="w-4 h-4" />;
      default: return null;
    }
  };

  const getTabLabel = (tab) => {
    switch (tab) {
      case 'categories': return 'Expense Categories';
      case 'payment_modes': return 'Payment Modes';
      case 'payers': return 'Payers';
      default: return tab;
    }
  };

  return (
    <div className="space-y-6" data-testid="expense-settings">
      {/* Backup & Restore */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Save className="w-5 h-5" />
            Data Management
          </CardTitle>
          <CardDescription>
            Backup and restore your expense data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px] p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Backup Data</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Download a complete backup of all expenses, settings, and audit logs.
              </p>
              <Button onClick={handleBackup} disabled={loading} className="gap-2">
                <Download className="w-4 h-4" />
                Download Backup
              </Button>
            </div>
            
            <div className="flex-1 min-w-[200px] p-4 border rounded-lg">
              <h4 className="font-medium mb-2">Restore Data</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Upload a backup file to restore the application state.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleRestoreFile}
              />
              <Button 
                variant="outline" 
                onClick={() => {
                  // Bug 3 & 5 Fix: Show permission error for non-super admins
                  if (!isSuperAdmin) {
                    toast.error('Only Super Admin can restore backups');
                    return;
                  }
                  fileInputRef.current?.click();
                }} 
                disabled={loading}
                className="gap-2"
              >
                <Upload className="w-4 h-4" />
                Restore from Backup
              </Button>
              {!isSuperAdmin && (
                <p className="text-xs text-amber-600 mt-2">
                  Only Super Admin can perform backup operations
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LOV Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            Dropdown Management (LOV)
          </CardTitle>
          <CardDescription>
            Manage the values available in dropdown menus throughout the app
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="categories" className="gap-2">
                <Tag className="w-4 h-4" />
                Categories
              </TabsTrigger>
              <TabsTrigger value="payment_modes" className="gap-2">
                <CreditCard className="w-4 h-4" />
                Payment Modes
              </TabsTrigger>
              <TabsTrigger value="payers" className="gap-2">
                <Users className="w-4 h-4" />
                Payers
              </TabsTrigger>
            </TabsList>
            
            {['categories', 'payment_modes', 'payers'].map(tab => (
              <TabsContent key={tab} value={tab} className="mt-4">
                <div className="space-y-4">
                  {/* Add New - Bug 9 Fix: Better placeholder text */}
                  <div className="flex gap-2">
                    <Input
                      placeholder={
                        tab === 'categories' 
                          ? 'Enter new category name (e.g., Food, Travel, Utilities)' 
                          : tab === 'payment_modes' 
                            ? 'Enter new payment mode (e.g., Cash, Credit Card, UPI)' 
                            : 'Enter new payer name (e.g., John, Company Card)'
                      }
                      value={newItem}
                      onChange={(e) => setNewItem(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
                      data-testid={`add-${tab}-input`}
                    />
                    <Button onClick={handleAddItem} disabled={loading} data-testid={`add-${tab}-btn`}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <Separator />
                  
                  {/* Items List */}
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    ) : lovData[tab]?.length === 0 ? (
                      <p className="text-center py-8 text-muted-foreground">
                        No items found. Add your first item above.
                      </p>
                    ) : (
                      lovData[tab]?.map((item, index) => (
                        <div 
                          key={item.id} 
                          className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 group"
                        >
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="w-8 h-8 rounded-full flex items-center justify-center">
                              {index + 1}
                            </Badge>
                            <span>{item.value}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                            onClick={() => setDeleteConfirm({ 
                              open: true, 
                              type: tab, 
                              id: item.id, 
                              value: item.value 
                            })}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-muted-foreground pt-2 border-t">
                    <span>Total: {lovData[tab]?.length || 0} items</span>
                    <span>{getTabLabel(tab)}</span>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => {
        if (!open) setDeleteConfirm({ open: false, type: '', id: '', value: '' });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Remove Item
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove "{deleteConfirm.value}"? This may affect existing expenses using this value.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirm({ open: false, type: '', id: '', value: '' })}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemoveItem} disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpenseSettings;
